import { SharedAssessmentKeys } from 'hydra-data-types';
import { StoreStateInternal } from '../store/Store';
import {
  hasOriginalMedicareAnswer,
  isExcludedFromMedicareAccess,
  isMedicareAdvantage,
  isUs65Plus,
  isUsUnder65,
  livesInMedicareAccessState,
} from './MedicareAccess';

/** The default `nextScreenSelector`.
 * Returns the first answer given on the current screen. */
export function getScreenAnswers(
  state: StoreStateInternal,
  currentScreenName: string
): string[] | undefined {
  return state.userData[currentScreenName as SharedAssessmentKeys];
}

export type AuthMethodUsed = 'loginAndAlreadyPaid' | 'register' | 'login';

/** Returns the key for Auth screen conditional nextScreen; use as nextScreenSelector. */
export function getAuthMethodUsed(state: StoreStateInternal): AuthMethodUsed | undefined {
  const { user } = state;
  if (user?.userId === undefined) {
    return undefined;
  }
  if (user.hasPaid) {
    return 'loginAndAlreadyPaid';
  }
  return user.authMethodUsed;
}

/** Returns if the user answered 'Yes' on the the doctor screen. */
export function getHasDoctor(state: StoreStateInternal): 'hasDoctor' | 'noDoctor' {
  return state.userData.doctor?.[0] === 'Yes' ? 'hasDoctor' : 'noDoctor';
}

/** Returns the key for Payment screen conditional nextScreen; use as nextScreenSelector. Routes by user.setUserPassword (needs to finish account vs already complete). */
export function getPaymentRoute(state: StoreStateInternal): 'newUser' | 'existingUser' {
  return state.user?.setUserPassword === true ? 'newUser' : 'existingUser';
}

export function getIsWearableProvided(state: StoreStateInternal): 'hasWearable' | undefined {
  return state.entry.referral?.isWearableProvided === true ? 'hasWearable' : undefined;
}

export function getIsPapReferral(state: StoreStateInternal): 'isPapReferral' | undefined {
  return state.entry.referral?.isPapReferral === true ? 'isPapReferral' : undefined;
}

export type PaymentPathRoute =
  | 'medicareOriginal'
  | 'medicareAdvantage'
  | 'medicareOther'
  | 'collectInsurance'
  | 'accessCode'
  | 'asl'
  | 'pap'
  | 'payment';

/** Resolves the non-Medicare branch: access code → ASL → PAP → Stripe payment, in priority order. */
export function getReferralPaymentRoute(
  state: StoreStateInternal
): 'accessCode' | 'asl' | 'pap' | 'payment' {
  const { referral, aslPaymentLinkId } = state.entry;
  if (referral?.hasAccessCode === true && !referral.isAslReferral) return 'accessCode';
  if (aslPaymentLinkId !== undefined) return 'asl';
  // Skip PAP flow if already completed — falls through to Payment
  if (state.pap.scoreResult === undefined && getIsPapReferral(state) !== undefined) return 'pap';
  return 'payment';
}

/**
 * The single payment-path decision. Returns the branch a user should take after Program Overview,
 * in strict priority order. US 65+ is checked FIRST so a senior who also arrives via an ASL URL is
 * routed toward the Medicare path rather than the ASL Stripe paywall. 65+ before answering the
 * health-cover screen resolve to 'medicareOther' (no type yet) — Program Overview routes that to the
 * health-cover screen; once answered we route by type. 'medicareOriginal' reflects the answer alone,
 * because the state screen it leads to decides whether the user can actually join ACCESS.
 *
 * Everyone else collects insurance before paying: US under-65 always, and non-US users (e.g. AU) on
 * the standard payment path. Access-code / ASL / PAP users keep their own route (no insurance step).
 */
export function getPaymentPathRoute(state: StoreStateInternal): PaymentPathRoute {
  if (isUs65Plus(state) === true) {
    if (hasOriginalMedicareAnswer(state) === true) {
      return isExcludedFromMedicareAccess(state) === true ? 'payment' : 'medicareOriginal';
    }
    if (isMedicareAdvantage(state) === true) return 'medicareAdvantage';
    return 'medicareOther';
  }
  if (isUsUnder65(state) === true) return 'collectInsurance';
  const referralRoute = getReferralPaymentRoute(state);
  return referralRoute === 'payment' ? 'collectInsurance' : referralRoute;
}

/**
 * Routes the state screen that follows a 65+ Original Medicare answer: into ACCESS in an ACCESS
 * state, otherwise on as if they hadn't picked Original Medicare.
 */
export function getMedicareAccessStateRoute(
  state: StoreStateInternal
): 'medicareOriginal' | 'notInAccessState' | undefined {
  if (!isUs65Plus(state)) return undefined;
  return livesInMedicareAccessState(state) ? 'medicareOriginal' : 'notInAccessState';
}
